import { Check } from 'lucide-react';

import { cn } from '@/shared/utils';

interface CreatorsMatchedFeatureFilterProps {
  features: string[];
  selectedFeatures: string[];
  onSelectedFeaturesChange: (features: string[]) => void;
  className?: string;
}

export function CreatorsMatchedFeatureFilter({
  features,
  selectedFeatures,
  onSelectedFeaturesChange,
  className,
}: CreatorsMatchedFeatureFilterProps) {
  if (features.length === 0) return null;

  const selectedFeatureSet = new Set(selectedFeatures);
  const selectedCount = selectedFeatures.length;

  const handleFeatureToggle = (feature: string) => {
    if (selectedFeatureSet.has(feature)) {
      onSelectedFeaturesChange(
        selectedFeatures.filter((selectedFeature) => selectedFeature !== feature),
      );
      return;
    }

    onSelectedFeaturesChange([...selectedFeatures, feature]);
  };

  return (
    <div className={cn('bg-background border-b px-6 py-2', className)}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <p className="text-foreground shrink-0 text-sm font-medium">Matched features</p>

        <div className="flex min-w-0 flex-1 flex-wrap gap-2">
          <button
            type="button"
            aria-pressed={selectedCount === 0}
            onClick={() => onSelectedFeaturesChange([])}
            className={cn(
              'inline-flex max-w-full items-center rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
              selectedCount === 0
                ? 'border-foreground bg-foreground text-background'
                : 'border-border bg-background text-foreground hover:bg-muted',
            )}
          >
            All results
          </button>

          {features.map((feature) => {
            const isSelected = selectedFeatureSet.has(feature);

            return (
              <button
                key={feature}
                type="button"
                aria-pressed={isSelected}
                onClick={() => handleFeatureToggle(feature)}
                className={cn(
                  'inline-flex max-w-full items-center rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                  isSelected
                    ? 'border-foreground bg-foreground text-background'
                    : 'border-border bg-background text-foreground hover:bg-muted',
                )}
              >
                <span className="flex min-w-0 items-center gap-1.5">
                  <span
                    className={cn(
                      'flex size-3.5 shrink-0 items-center justify-center rounded-full border transition-colors',
                      isSelected
                        ? 'border-background/70 bg-background/15 text-background'
                        : 'border-muted-foreground/40 bg-background text-transparent',
                    )}
                  >
                    <Check
                      className={cn('size-2.5 shrink-0', isSelected ? 'opacity-100' : 'opacity-0')}
                    />
                  </span>
                  <span className="min-w-0 text-left break-words whitespace-normal">{feature}</span>
                </span>
              </button>
            );
          })}
        </div>

        {selectedCount > 0 && (
          <p className="text-muted-foreground shrink-0 text-xs">{selectedCount} selected</p>
        )}
      </div>
    </div>
  );
}
